import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { isPlatform } from '@ionic/react'
import { Notifications as NotificationsIcon, NotificationsOutlined } from '@mui/icons-material'
import { Badge } from '@mui/material'
import React, { useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import useUser from '../../../../contexts/hooks/useUser'
import { ModalContext } from '../../../../contexts/ModalContext'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import { backendCall } from '../../../../modules/backendApi'
import { trpc } from '../../../../modules/trpc'
import { getInterventionIdsFromList } from '../../../../utils/getInterventionIdsWithoutDuplicates'

interface ContainerProps {}

const NotificationsBell: React.FC<ContainerProps> = () => {
	const { user } = useUser()

	const object = useContext(ObjectContext)
	const { insertModal } = useContext(ModalContext)
	const history = useHistory()

	const notificationsQuery = trpc.user.notifications.get.useQuery(
		{ userId: user?.id ?? 0 },
		{ initialData: [], enabled: !!user?.id }
	)
	const { data: vehicles } = trpc.eNode.vehicles.get.useQuery(
		{
			hashedPid: user?.hashedPid ?? '',
		},
		{ enabled: !!user?.hashedPid }
	)

	const interventionList = getInterventionIdsFromList(vehicles ?? [])

	const addListeners = async () => {
		if (isPlatform('desktop') || !Capacitor.isNativePlatform()) return

		await PushNotifications.removeAllListeners()

		await PushNotifications.addListener('registration', token => {
			console.info('Registration token: ', token.value)
			if (user && user?.fcmRegToken !== token.value)
				backendCall.edit('user', user.id, { fcmRegToken: token.value })
		})

		await PushNotifications.addListener('registrationError', err => {
			console.error('Registration error: ', err.error)
		})

		await PushNotifications.addListener('pushNotificationReceived', notification => {
			console.log('Push notification received: ', notification)
		})

		await PushNotifications.addListener('pushNotificationActionPerformed', notification => {
			console.log('Push notification action performed', notification)

			if (notification.actionId !== 'tap') return

			const savedNoti = notification.notification.data.saved === 'true'
			const linkTarget = notification.notification.data.linkTarget
			const modalLinkTarget = notification.notification.data.modalLinkTarget
			const modalVariable = notification.notification.data.modalVariable

			if (savedNoti) {
				notificationsQuery.refetch()
				if (linkTarget) {
					history.push(linkTarget)
				} else if (modalLinkTarget) {
					if (modalVariable && modalLinkTarget === 'electricCarDetail') {
						history.push(`/devices/electric-car/${modalVariable}`)
					} else {
						insertModal(modalLinkTarget)
					}
				} else {
					insertModal('notifications')
				}
			}
		})
	}

	const registerNotifications = async () => {
		if (isPlatform('desktop') || !Capacitor.isNativePlatform()) return

		let permStatus = await PushNotifications.checkPermissions()

		if (permStatus.receive === 'prompt') permStatus = await PushNotifications.requestPermissions()
		if (permStatus.receive !== 'granted') return console.error('User denied permissions!')

		await PushNotifications.register()
	}

	useEffect(() => {
		addListeners()
		registerNotifications()
		// eslint-disable-next-line
	}, [])

	const alertCount = !user
		? 0
		: notificationsQuery.data.reduce((acc: number, cur) => acc + (cur.isRead ? 0 : 1), 0) +
			interventionList.length

	const onClick = () => {
		history.push('/notifications')
	}

	return (
		<div onClick={onClick} className='button-hover'>
			<Badge badgeContent={alertCount} color='error' overlap='circular'>
				{alertCount > 0 ? (
					<NotificationsIcon fontSize='large' className='text-white' />
				) : (
					<NotificationsOutlined fontSize='large' className='text-white' />
				)}
			</Badge>
		</div>
	)
}

export default NotificationsBell
